import { linkToDeviceDetail } from "#routers/links";
import { linkToEquipmentDetail } from "#routes/organization/equipment/[equipmentId]";
import { linkToEstimationMethodDetail } from "#routes/organization/equipment/[equipmentId]/estimation-method/[methodId]/detail";
import { linkToEstimationMethodCalculationResult } from "#routes/organization/equipment/[equipmentId]/estimation-method/[methodId]/result/[resultId]/detail";
import { EquipmentRecordDetailRoutePath } from "#routes/organization/equipment/[equipmentId]/record-details/[recordId]";
import { EquipmentRecordValueDetailRoutePath } from "#routes/organization/equipment/[equipmentId]/record-details/[recordId]/value/[measurementType]";
import { EquipmentRecordValueSourceRoutePath } from "#routes/organization/equipment/[equipmentId]/record-details/[recordId]/value/[measurementType]/add-source";
import { linkToFacilityDetail } from "#routes/organization/facilities/[facilityId]";
import { linkToFacilityRecordDetail } from "#routes/organization/facilities/[facilityId]/record/[recordId]/details";
import { linkToFacilityRecordValueDetail } from "#routes/organization/facilities/[facilityId]/record/[recordId]/value/[measurementType]";
import { linkToAddFacilityRecordValueSource } from "#routes/organization/facilities/[facilityId]/record/[recordId]/value/[measurementType]/add-source";
import { FlowDetailRoute } from "#routes/organization/flows/[flowId]/detail";
import { FlowEstimationMethodDetailRoute } from "#routes/organization/flows/[flowId]/detail/estimation-details/[estimationMethodId]";
import { FlowEstimationMethodResultDetailRoute } from "#routes/organization/flows/[flowId]/detail/estimation-details/[estimationMethodId]/result/[yearMonth]";
import { FlowRecordDetailRoute } from "#routes/organization/flows/[flowId]/detail/record/[recordId]";
import { FlowRecordValueDetailRoute } from "#routes/organization/flows/[flowId]/detail/record/[recordId]/value/[measurementType]";
import { FlowRecordValueSourceRoute } from "#routes/organization/flows/[flowId]/detail/record/[recordId]/value/[measurementType]/source";
import { NetworkDetailRoute } from "#routes/organization/networks/[networkId]/detail";
import { NetworkCalculationResultRoute } from "#src/routes/organization/networks/[networkId]/detail/calculation-result/[period]/details";
import { AssetType, type AssetTypeType } from "@validereinc/domain";

export const linkToAssetDetailPage = (
  assetType: AssetTypeType,
  assetId: string
) => {
  switch (assetType) {
    case AssetType.FACILITY:
      return linkToFacilityDetail(assetId);
    case AssetType.EQUIPMENT:
      return linkToEquipmentDetail(assetId);
    case AssetType.DEVICE:
      return linkToDeviceDetail(assetId);
    case AssetType.FLOW:
      return FlowDetailRoute.toLink({ pathParams: { flowId: assetId } });
    case AssetType.ASSET_GROUP:
      return NetworkDetailRoute.toLink({ pathParams: { networkId: assetId } });
    default:
      console.error(
        `No asset detail page link configured for assetType=${assetType}`
      );
      return "";
  }
};
export const linkToCalculationDetailPage = (
  assetType: AssetTypeType,
  assetId: string,
  estimationMethodId: string
) => {
  switch (assetType) {
    case AssetType.EQUIPMENT:
      return linkToEstimationMethodDetail(estimationMethodId, assetId);
    case AssetType.FLOW:
      return FlowEstimationMethodDetailRoute.toLink({
        pathParams: { flowId: assetId, estimationMethodId },
      });
    case AssetType.ASSET_GROUP:
      // TODO: Need to include query parameter for "calculations" tab
      return NetworkDetailRoute.toLink({ pathParams: { networkId: assetId } });
    default:
      return "";
  }
};

export const linkToCalculationResultDetailPage = (
  assetType: AssetTypeType,
  assetId: string,
  estimationMethodId: string,
  yearMonth: string
) => {
  switch (assetType) {
    case AssetType.ASSET_GROUP:
      return NetworkCalculationResultRoute.toLink({
        pathParams: {
          networkId: assetId,
          period: yearMonth,
        },
      });
    case AssetType.EQUIPMENT:
      return linkToEstimationMethodCalculationResult(
        estimationMethodId,
        assetId,
        yearMonth
      );
    case AssetType.FLOW:
      return FlowEstimationMethodResultDetailRoute.toLink({
        pathParams: {
          flowId: assetId,
          estimationMethodId,
          yearMonth,
        },
      });
    default:
      return "";
  }
};

export const linkToEstimationMethodDetailPage = (
  assetType: AssetTypeType,
  assetId: string,
  estimationMethodId: string
) => {
  switch (assetType) {
    case AssetType.EQUIPMENT:
      return linkToEstimationMethodDetail(estimationMethodId, assetId);
    case AssetType.FLOW:
      return FlowEstimationMethodDetailRoute.toLink({
        pathParams: { flowId: assetId, estimationMethodId },
      });
    default:
      console.error(
        `No estimation method detail page link configured for assetType=${assetType}`
      );
      return "";
  }
};

export const linkToRecordDetail = (
  assetType: AssetTypeType,
  assetId: string,
  recordId: string
) => {
  switch (assetType) {
    case AssetType.FACILITY:
      return linkToFacilityRecordDetail(assetId, recordId);
    case AssetType.EQUIPMENT:
      return EquipmentRecordDetailRoutePath.toLink({
        pathParams: {
          equipmentId: assetId,
          recordId,
        },
      });
    case AssetType.FLOW:
    default:
      return FlowRecordDetailRoute.toLink({
        pathParams: {
          flowId: assetId,
          recordId,
        },
      });
  }
};
export const linkToRecordValueDetail = (
  assetType: AssetTypeType,
  assetId: string,
  recordId: string,
  measurementType: string
) => {
  switch (assetType) {
    case AssetType.FACILITY:
      return linkToFacilityRecordValueDetail(
        assetId,
        recordId,
        measurementType
      );
    case AssetType.EQUIPMENT:
      return EquipmentRecordValueDetailRoutePath.toLink({
        pathParams: { equipmentId: assetId, recordId, measurementType },
      });
    case AssetType.FLOW:
    default:
      return FlowRecordValueDetailRoute.toLink({
        pathParams: {
          flowId: assetId,
          recordId,
          measurementType,
        },
      });
  }
};

export const linkToRecordValueSourceDetail = (
  assetType: AssetTypeType,
  assetId?: string,
  recordId?: string,
  measurementType?: string
): string => {
  switch (assetType) {
    case AssetType.FACILITY:
      return linkToAddFacilityRecordValueSource(
        assetId ?? "",
        recordId,
        measurementType
      );
    case AssetType.EQUIPMENT:
      return EquipmentRecordValueSourceRoutePath.toLink({
        pathParams: {
          equipmentId: assetId ?? "",
          recordId,
          measurementType,
        },
      });
    case AssetType.FLOW:
    default:
      return FlowRecordValueSourceRoute.toLink({
        pathParams: {
          flowId: assetId ?? "",
          recordId,
          measurementType,
        },
      });
  }
};
